import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { GridComponentOption, TooltipComponentOption } from 'echarts/types/dist/components';
import {
  DataZoomComponentOption,
  EChartsOption,
  LegendComponentOption,
  TitleOption,
  XAXisOption,
  YAXisOption,
  ZRColor,
} from 'echarts/types/dist/shared';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { ChartColors } from '@nimbus-library/features/charts/models/chart-colors';
import { ThemedChartRecordModel } from '@nimbus-library/features/charts/models/chart-record.model';
import { ThemedChartSeriesModel } from '@nimbus-library/features/charts/models/chart-series.model';
import { ThemedChartProperties } from '@nimbus-library/features/charts/models/chart.model';
import { EchartLegendOption, EchartLegendOptionDefault } from '@nimbus-library/features/charts/models/echart/echart-legend-option.model';
import { EchartLegendPosition } from '@nimbus-library/features/charts/models/echart/echart-legend-position.enum';
import { EchartTitleOption, EchartTitleOptionDefault } from '@nimbus-library/features/charts/models/echart/echart-title-option.model';
import { EchartTooltipItem } from '@nimbus-library/features/charts/models/echart/echart-tooltip-item.model';
import { ThemedHeatmapRecordModel } from '@nimbus-library/features/charts/models/heatmap-record.model';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';
import { DateFormat } from '@nimbus-library/features/localization/models/enums/language.enum';

@Injectable({ providedIn: 'root' })
export class EchartService {
  public constructor(private store: Store) {}

  public getDateTimeFormat(
    type: 'date' | 'dateShort' | 'dateShortTime' | 'dateTime' | 'time',
    dateFormat: DateFormat = null,
  ): Intl.DateTimeFormat {
    function getLocale(d: string): string {
      switch (d) {
        case DateFormat.German:
          return DateFormat.Danish;
        case DateFormat.Spanish:
        case DateFormat.Italian:
          return DateFormat.French;
        default:
          return d;
      }
    }

    const locale = getLocale(dateFormat || this.store.selectSnapshot(LocalizationState.selectedDateFormat));

    switch (type) {
      case 'date':
        return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
      case 'dateShort':
        return new Intl.DateTimeFormat(locale, { month: '2-digit', day: '2-digit' });
      case 'dateShortTime':
        return new Intl.DateTimeFormat(locale, { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
      case 'time':
        return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
      case 'dateTime':
        return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
      default:
        return new Intl.DateTimeFormat(locale);
    }
  }

  public getChartTheme(theme: ChartTheme): string {
    return theme === ChartTheme.Dark ? 'dark' : 'light';
  }

  public getChartFgColor(theme: ChartTheme): string {
    return theme === ChartTheme.Dark ? ChartColors.WhiteDark : ChartColors.DarkGunmetal;
  }

  public getChartBgTooltipColor(theme: ChartTheme): string {
    return theme === ChartTheme.Dark ? ChartColors.DarkGunmetalDark : ChartColors.White;
  }

  public getChartBgAltColor(theme: ChartTheme): ZRColor {
    return theme === ChartTheme.Dark ? 'rgba(0, 0, 0, 0.14)' : 'rgba(0, 0, 0, 0.04)';
  }

  public createChartOptionBase(backgroundColor: ZRColor = 'transparent'): EChartsOption {
    return {
      animation: false,
      backgroundColor,
      textStyle: { fontFamily: 'Segoe UI', fontWeight: 700, fontSize: '0.625rem' },
    };
  }

  public createDataZoomComponentOptionBase(): DataZoomComponentOption {
    return {
      type: 'inside',
      throttle: 50,
    };
  }

  public createTooltipOption(
    theme: ChartTheme,
    show: boolean,
    trigger: 'item' | 'axis' | 'none',
    confine: boolean = false,
  ): TooltipComponentOption {
    return {
      show,
      trigger,
      confine,
      backgroundColor: this.getChartBgTooltipColor(theme),
      textStyle: { fontWeight: 'normal', fontSize: 11, color: this.getChartFgColor(theme) },
      borderWidth: 0,
      borderRadius: 2,
      padding: [8, 10],
      extraCssText: 'will-change: unset;',
    };
  }

  public createTooltipHtml(items: EchartTooltipItem[]): string {
    const content = items
      .map((item, i) => {
        const marker = item.color
          ? `<span style="display: inline-block;` +
            ` margin-right: 4px; border-radius: 10px; width: 10px; height: 10px; background-color: ${item.color};"></span>`
          : '';
        const label = item.label ? `<span style="margin-left: 2px;">${item.label.toString().trim()}</span>` : '';
        const value = item.value ? `<span style="margin-left: 20px;">${item.value.toString().trim()}</span>` : '';
        return `<div style="line-height: 1; margin-top: ${
          i === 0 ? 0 : 8
        }px;">${marker}${label}${value}<div style="clear: both;"></div></div>`;
      })
      .join('');
    return `<div style="white-space: nowrap;">${content}</div>`;
  }

  public createGridOption(legendOption: EchartLegendOption = EchartLegendOptionDefault, containLabel: boolean = true): GridComponentOption {
    const calculateRightOffset = (legendXPosition: string | number): string => {
      if (typeof legendXPosition === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return `${100 - parseInt(legendXPosition)}%`;
      } else {
        return String(legendXPosition);
      }
    };

    return {
      top: '10',
      right:
        legendOption.position === EchartLegendPosition.Right
          ? legendOption.xPosition
            ? calculateRightOffset(legendOption.xPosition)
            : '14%'
          : '15',
      bottom: legendOption.position === EchartLegendPosition.Bottom ? '10%' : '10',
      left: '15',
      containLabel,
    };
  }

  public createTitleOption(
    theme: ChartTheme,
    titleOption: EchartTitleOption = EchartTitleOptionDefault,
    legendOption: EchartLegendOption = EchartLegendOptionDefault,
  ): TitleOption {
    function getPositionTop(): string | number {
      switch (legendOption.position) {
        case EchartLegendPosition.Right:
          return titleOption.yPosition || '5%';
        case EchartLegendPosition.Bottom:
          return titleOption.yPosition || '60%';
        default:
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          return 1.5;
      }
    }

    function getPositionLeft(): string | number {
      switch (legendOption.position) {
        case EchartLegendPosition.Right:
          return titleOption.xPosition || '60%';
        case EchartLegendPosition.Bottom:
          return titleOption.xPosition || 'center';
        default:
          return 'center';
      }
    }

    return {
      show: !!titleOption.title,
      padding: 0,
      top: getPositionTop(),
      left: getPositionLeft(),
      text: titleOption.title ? (titleOption.skipUpperCase ? titleOption.title : titleOption.title.toUpperCase()) : '',
      textStyle: {
        fontSize: titleOption.fontSize || '0.7rem',
        fontWeight: titleOption.fontWeight,
        color: this.getChartFgColor(theme),
      },
    };
  }

  public createLegendOption(
    theme: ChartTheme,
    titleOption: EchartTitleOption = EchartTitleOptionDefault,
    legendOption: EchartLegendOption = EchartLegendOptionDefault,
    legendIcon: string = 'circle',
    legendData: (string | { name: string; icon?: string })[] = undefined,
    legendDataValueWidth: number = undefined,
    formatter: (name: string) => string = undefined,
  ): LegendComponentOption {
    function getPositionTop(): string | number {
      switch (legendOption.position) {
        case EchartLegendPosition.Right: {
          if (legendOption.yPosition && legendOption.keepYPosition) {
            return legendOption.yPosition;
          }
          if (legendOption.yPosition || titleOption.title) {
            return '21%';
          }
          return 'middle';
        }
        case EchartLegendPosition.Bottom:
          return legendOption.yPosition || '66%';
        default:
          return '0';
      }
    }

    function getPositionLeft(): string | number {
      switch (legendOption.position) {
        case EchartLegendPosition.Right:
          return legendOption.xPosition || '60%';
        case EchartLegendPosition.Bottom:
          return legendOption.xPosition || 'center';
        default:
          return '0';
      }
    }

    return {
      show: legendOption.position !== EchartLegendPosition.None,
      type: legendOption.type || 'plain',
      orient: 'vertical',
      padding: legendOption.padding || 0,
      itemGap: 4,
      itemHeight: 10,
      itemWidth: legendOption.itemWidth || 24,
      icon: legendIcon,
      top: getPositionTop(),
      left: getPositionLeft(),
      data: legendData,
      formatter: formatter || ((name): string => (name || '').toUpperCase()),
      textStyle: {
        fontSize: legendOption.fontSize || '0.75rem',
        color: this.getChartFgColor(theme),
        opacity: 0.6,
        width: legendOption.textWidth,
        overflow: legendOption.textOverflow,
        rich: {
          value: {
            fontSize: legendOption.fontSize || '0.75rem',
            color: this.getChartFgColor(theme),
            opacity: 0.6,
            width: legendDataValueWidth,
          },
        },
      },
      pageButtonGap: 15,
      pageIcons: {
        horizontal: [
          'M1305.032-117.19l-742.968 742.968 742.968 742.968 120.604-120.604-622.364-622.364 622.364-622.364-120.604-120.604z',
          'M742.968-117.19l-120.604 120.604 622.364 622.364-622.364 622.364 120.604 120.604 742.968-742.968-742.968-742.968z',
        ],
        vertical: [
          'M1024 163.84l-742.968 742.968 120.604 120.604 622.364-622.364 622.364 622.364 120.604-120.604-742.968-742.968z',
          'M1646.364 224.142l-622.364 622.364-622.364-622.364-120.604 120.604 742.968 742.968 742.968-742.968-120.604-120.604z',
        ],
      },
      pageIconColor: theme === ChartTheme.Dark ? ChartColors.FontDark : ChartColors.Font,
      pageIconInactiveColor: theme === ChartTheme.Dark ? ChartColors.DimGrey : ChartColors.AshGreyPale,
      pageIconSize: 12,
      pageTextStyle: {
        fontSize: 10,
        color: theme === ChartTheme.Dark ? ChartColors.FontDark : ChartColors.Font,
      },
    };
  }

  public createXAxisOptionBase(boundaryGap: boolean): XAXisOption {
    return {
      type: 'category',
      boundaryGap,
      axisLabel: { fontSize: '0.625rem' },
    };
  }

  public createYAxisOptionBase(): YAXisOption {
    return {
      type: 'value',
      axisLabel: { fontSize: '0.625rem' },
    };
  }

  public createYAxisOptions(data: ThemedChartProperties, useValueAxis: boolean, maxNum: number = undefined): YAXisOption[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function createYAxisOption(valueAxis: any): YAXisOption {
      return {
        id: valueAxis.name,
        type: 'value',
        max: useValueAxis ? valueAxis.max : maxNum,
        interval: useValueAxis ? valueAxis.majorUnit : undefined,
        minInterval: 1,
        axisLabel: {
          fontSize: '0.625rem',
          formatter: valueAxis.labels && valueAxis.labels.format ? valueAxis.labels.format.replace('{0}', '{value}') : undefined,
        },
      };
    }

    const yAxisOptions: YAXisOption[] = [];
    if (data.valueAxis && !Array.isArray(data.valueAxis)) {
      yAxisOptions.push(createYAxisOption(data.valueAxis));
    }
    if (data.valueAxis && Array.isArray(data.valueAxis)) {
      (data.valueAxis as unknown[]).forEach((x) => yAxisOptions.push(createYAxisOption(x)));
    }
    if (data.customValuesAxes) {
      data.customValuesAxes.forEach((x) => yAxisOptions.push(createYAxisOption(x)));
    }
    return yAxisOptions;
  }

  public createSeriesOptionBase(
    series: ThemedChartSeriesModel<ThemedChartRecordModel> | ThemedChartSeriesModel<ThemedHeatmapRecordModel>,
    data: unknown[],
  ): unknown {
    return {
      yAxisId: series.axisId,
      name: series.name,
      color: series.color,
      data,
    };
  }
}
